import { useCallback, useEffect, useRef, useState } from 'react'

import { isTextFieldFocused, readPref, STORAGE_PREFIX, writePref } from '@/app/lib/paneKeys'

/** Below this viewport width a rail defaults to collapsed, but only when nothing is stored. */
const NARROW_VIEWPORT_QUERY = '(max-width: 899px)'

/** Hover dwell before a collapsed rail peeks open, and the grace period before a peek closes. */
export const QUANT_PEEK_OPEN_MS = 220
export const QUANT_PEEK_CLOSE_MS = 280

export const QUANT_INDICATORS_KEY = `${STORAGE_PREFIX}quant-inds`
export const QUANT_ANALYST_KEY = `${STORAGE_PREFIX}quant-analyst`

export interface UseQuantRailOptions {
  storageKey: string
  bracket: '[' | ']'
  /** While true (the indicator modal is open) the shortcut is ignored and a peek never closes. */
  suspended?: boolean
}

export interface QuantRailState {
  open: boolean
  peeking: boolean
  toggle: () => void
  onPointerEnter: (event: { pointerType?: string }) => void
  onPointerLeave: () => void
}

function initialOpen(storageKey: string): boolean {
  const stored = readPref(storageKey)
  if (stored !== null) return stored === 'open'
  if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
    return !window.matchMedia(NARROW_VIEWPORT_QUERY).matches
  }
  return true
}

/**
 * One Manual trade rail's pin + hover-peek state (spec 036, handoff 4.1),
 * mirroring `usePaneCollapse`. Persisted ONLY in the rail's own
 * `qmc:quant-*` key and ONLY when the operator pins/collapses (a default is
 * never written back). The peek is never persisted; toggling during a peek pins.
 */
export function useQuantRail({ storageKey, bracket, suspended = false }: UseQuantRailOptions): QuantRailState {
  const [open, setOpen] = useState<boolean>(() => initialOpen(storageKey))
  const [peeking, setPeeking] = useState(false)
  const openTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const hovering = useRef(false)

  const clearTimers = useCallback(() => {
    if (openTimer.current !== null) clearTimeout(openTimer.current)
    if (closeTimer.current !== null) clearTimeout(closeTimer.current)
    openTimer.current = null
    closeTimer.current = null
  }, [])

  const toggle = useCallback(() => {
    clearTimers()
    const next = !open
    writePref(storageKey, next ? 'open' : 'closed')
    setOpen(next)
    setPeeking(false)
  }, [clearTimers, open, storageKey])

  useEffect(() => {
    if (suspended) return
    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== bracket || event.ctrlKey || event.metaKey || event.altKey) return
      if (isTextFieldFocused(document.activeElement)) return
      event.preventDefault()
      toggle()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [bracket, suspended, toggle])

  useEffect(() => clearTimers, [clearTimers])

  const scheduleClose = useCallback(() => {
    if (closeTimer.current !== null) return
    closeTimer.current = setTimeout(() => {
      closeTimer.current = null
      setPeeking(false)
    }, QUANT_PEEK_CLOSE_MS)
  }, [])

  // The modal closed while the pointer is elsewhere: let the peek go.
  useEffect(() => {
    if (!suspended && peeking && !hovering.current) scheduleClose()
  }, [suspended, peeking, scheduleClose])

  const onPointerEnter = useCallback(
    (event: { pointerType?: string }) => {
      if (event.pointerType === 'touch') return
      hovering.current = true
      if (closeTimer.current !== null) {
        clearTimeout(closeTimer.current)
        closeTimer.current = null
      }
      if (open || openTimer.current !== null) return
      openTimer.current = setTimeout(() => {
        openTimer.current = null
        setPeeking(true)
      }, QUANT_PEEK_OPEN_MS)
    },
    [open],
  )

  const onPointerLeave = useCallback(() => {
    hovering.current = false
    if (openTimer.current !== null) {
      clearTimeout(openTimer.current)
      openTimer.current = null
    }
    if (suspended) return
    scheduleClose()
  }, [scheduleClose, suspended])

  return { open, peeking: peeking && !open, toggle, onPointerEnter, onPointerLeave }
}
